/**
 * "Sync by sound" for a multicam set: reads the opening minutes of the
 * master's sound and of each other angle's (only the audio bytes, through
 * range requests), finds each angle's offset from the master, and turns it
 * into the set's timeline start and trim.
 *
 * A short window is tried first (most cameras are started within a minute or
 * two of each other); a weak match is retried over a longer one before it is
 * reported as weak. Weak matches are never applied automatically.
 */

import { ByteSource, urlByteSource } from './byteSource';
import { PcmClip, ReadAudioOptions, StreamDecoder } from './audioDecode';
import { AngleTiming, SyncEstimate, estimateOffset, timingForOffset } from './audioSync';
import { MediaReadError } from './isoAudio';
import { MediaAudioTrack, openMediaAudio, readTrackClip } from './mediaAudio';
import type { PacketDecoder } from './xiphDecode';

/** Sample rate used for matching: plenty for speech rhythm, small in memory. */
export const SYNC_SAMPLE_RATE = 8000;
const FIRST_WINDOW_S = 240;
const SECOND_WINDOW_S = 720;
/**
 * A WebM/MKV has no index of its sound, so its opening minutes are read
 * video and all; past this much, a file is lined up by hand instead.
 */
export const SYNC_MAX_SCAN_BYTES = 400 * 1024 * 1024;

export interface SyncSource {
  key: string;
  /** Where the file is; used unless `source` is given. */
  url?: string;
  source?: ByteSource;
  /** The angle's current trim, kept where possible. */
  sourceInMs: number;
}

export type AngleSyncResult =
  | { key: string; status: 'matched'; estimate: SyncEstimate; timing: AngleTiming }
  | { key: string; status: 'weak'; estimate: SyncEstimate }
  | { key: string; status: 'failed'; reason: string };

export interface SyncProgress {
  /** 0..1 over the whole job. */
  fraction: number;
  /** The angle being read: the master's key while reading the master. */
  key: string;
}

export interface SyncOptions {
  signal?: AbortSignal;
  onProgress?: (progress: SyncProgress) => void;
  /** Called as each angle finishes, so results can show one at a time. */
  onResult?: (result: AngleSyncResult) => void;
  decoder?: StreamDecoder;
  /** Opus/Vorbis packets (WebM): replaces WebCodecs. */
  packetDecoder?: PacketDecoder;
  openSource?: (url: string, signal?: AbortSignal) => Promise<ByteSource>;
}

/** Plain-language reason for a failure, for the set-up screen. */
export const describeSyncFailure = (error: unknown): string => {
  if (error instanceof MediaReadError) {
    switch (error.code) {
      case 'not-mp4':
        return 'Sync by sound works with MP4, MOV, WebM and MKV videos, and WAV, MP3, M4A, AAC and AIFF recordings. Set this one by hand.';
      case 'no-audio':
        return 'This file has no sound to match. Set this one by hand.';
      case 'unsupported-audio':
        // The readers name the problem ("This browser can’t decode Opus sound.").
        return /can’t|in full/.test(error.message)
          ? `${error.message} Set this one by hand.`
          : 'This file’s sound format can’t be read in the browser. Set this one by hand.';
      default:
        return 'This file couldn’t be read. Set this one by hand.';
    }
  }
  const message = error instanceof Error ? error.message : '';
  if (/expired|not allowed/i.test(message)) return 'The link to this file has expired. Reload the page and try again.';
  if (/decode/i.test(message)) return 'This file’s sound couldn’t be decoded in this browser. Set this one by hand.';
  return 'This file couldn’t be read. Check your connection and try again.';
};

const isAbort = (error: unknown) => (error as { name?: string } | null)?.name === 'AbortError';

interface OpenedAudio {
  source: ByteSource;
  track: MediaAudioTrack;
}

/**
 * Matches every angle in `angles` against `master`. Failures are per angle:
 * one unreadable file doesn't stop the others.
 */
export const syncAnglesBySound = async (
  master: SyncSource,
  angles: SyncSource[],
  options: SyncOptions = {}
): Promise<AngleSyncResult[]> => {
  const { signal, onProgress, onResult, decoder, packetDecoder } = options;
  const openSource = options.openSource ?? ((url: string, sig?: AbortSignal) => urlByteSource(url, { signal: sig }));
  const units = angles.length + 1;
  const progress = (unit: number, fraction: number, key: string) =>
    onProgress?.({ fraction: Math.min(1, (unit + Math.min(1, Math.max(0, fraction))) / units), key });

  const open = async (input: SyncSource): Promise<OpenedAudio> => {
    const source = input.source ?? (await openSource(input.url ?? '', signal));
    const opened = await openMediaAudio(source, { signal, maxSeconds: SECOND_WINDOW_S + 5 });
    return { source: opened.source, track: opened.track };
  };
  const read = (audio: OpenedAudio, windowS: number, report?: ReadAudioOptions['onProgress']): Promise<PcmClip> =>
    readTrackClip(audio.source, audio.track, 0, windowS, SYNC_SAMPLE_RATE, {
      signal,
      decoder,
      packetDecoder,
      onProgress: report,
      maxScanBytes: SYNC_MAX_SCAN_BYTES,
    });

  let masterAudio: OpenedAudio;
  let masterClip: PcmClip;
  try {
    progress(0, 0, master.key);
    masterAudio = await open(master);
    masterClip = await read(masterAudio, FIRST_WINDOW_S, (f) => progress(0, f, master.key));
  } catch (error) {
    if (isAbort(error)) throw error;
    const reason = `The master angle couldn’t be read. ${describeSyncFailure(error)}`;
    const results = angles.map((angle): AngleSyncResult => ({ key: angle.key, status: 'failed', reason }));
    results.forEach((result) => onResult?.(result));
    return results;
  }
  let longMasterClip: PcmClip | null = null;

  const results: AngleSyncResult[] = [];
  for (let index = 0; index < angles.length; index += 1) {
    const angle = angles[index];
    const unit = index + 1;
    let result: AngleSyncResult;
    try {
      const audio = await open(angle);
      const clip = await read(audio, FIRST_WINDOW_S, (f) => progress(unit, f * 0.5, angle.key));
      let estimate = estimateOffset(masterClip, clip);
      const canWiden =
        masterAudio.track.durationSeconds > FIRST_WINDOW_S + 1 || audio.track.durationSeconds > FIRST_WINDOW_S + 1;
      if ((!estimate || estimate.quality === 'weak') && canWiden) {
        if (!longMasterClip) longMasterClip = await read(masterAudio, SECOND_WINDOW_S);
        const longClip = await read(audio, SECOND_WINDOW_S, (f) => progress(unit, 0.5 + f * 0.5, angle.key));
        const wider = estimateOffset(longMasterClip, longClip);
        if (wider && (!estimate || wider.score * wider.distinctness > estimate.score * estimate.distinctness)) {
          estimate = wider;
        }
      }
      if (!estimate) {
        result = { key: angle.key, status: 'failed', reason: 'There wasn’t enough sound to compare. Set this angle by hand.' };
      } else if (estimate.quality === 'weak') {
        result = { key: angle.key, status: 'weak', estimate };
      } else {
        result = {
          key: angle.key,
          status: 'matched',
          estimate,
          timing: timingForOffset(estimate.offsetS, master.sourceInMs, angle.sourceInMs),
        };
      }
    } catch (error) {
      if (isAbort(error)) throw error;
      result = { key: angle.key, status: 'failed', reason: describeSyncFailure(error) };
    }
    progress(unit, 1, angle.key);
    results.push(result);
    onResult?.(result);
  }
  return results;
};
